import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

/**
 * WABA (WhatsApp Business Account) Redux Slice
 * Manages user's WABA registration status and details
 */

// Thunks
export const fetchWABAStatus = createAsyncThunk(
  'waba/fetchStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.waba.getStatus();
      return response.data.data; // Returns { hasWABA, totalWABAs, wabaAccounts, primaryWABA }
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const requestWABAOtp = createAsyncThunk(
  'waba/requestOtp',
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiService.waba.requestOtp(data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const verifyWABAOtp = createAsyncThunk(
  'waba/verifyOtp',
  async ({ wabaId, otp }, { rejectWithValue }) => {
    try {
      const response = await apiService.waba.verifyOtp(wabaId, { otp });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const connectWABA = createAsyncThunk(
  'waba/connect',
  async ({ wabaId, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.waba.connect(wabaId, data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const disconnectWABA = createAsyncThunk(
  'waba/disconnect',
  async (wabaId, { rejectWithValue }) => {
    try {
      await apiService.waba.disconnect(wabaId);
      return wabaId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  hasWABA: false,
  totalWABAs: 0,
  wabaAccounts: [],
  primaryWABA: null,
  loading: false,
  error: null,
  setupInProgress: false,
};

// Slice
const wabaSlice = createSlice({
  name: 'waba',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetWABA: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch WABA Status
    builder
      .addCase(fetchWABAStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWABAStatus.fulfilled, (state, action) => {
        state.hasWABA = action.payload.hasWABA;
        state.totalWABAs = action.payload.totalWABAs;
        state.wabaAccounts = action.payload.wabaAccounts || [];
        state.primaryWABA = action.payload.primaryWABA || null;
        state.loading = false;
      })
      .addCase(fetchWABAStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.hasWABA = false;
      });

    // Request OTP
    builder
      .addCase(requestWABAOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.setupInProgress = true;
      })
      .addCase(requestWABAOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.setupInProgress = true;
      })
      .addCase(requestWABAOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Verify OTP
    builder
      .addCase(verifyWABAOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyWABAOtp.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(verifyWABAOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Connect WABA
    builder
      .addCase(connectWABA.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(connectWABA.fulfilled, (state, action) => {
        state.loading = false;
        state.setupInProgress = false;
        // Refresh status after connection
      })
      .addCase(connectWABA.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Disconnect WABA
    builder
      .addCase(disconnectWABA.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(disconnectWABA.fulfilled, (state, action) => {
        state.loading = false;
        state.wabaAccounts = state.wabaAccounts.filter(waba => waba.id !== action.payload);
        if (state.primaryWABA?.id === action.payload) {
          state.primaryWABA = state.wabaAccounts[0] || null;
          state.hasWABA = state.wabaAccounts.length > 0;
        }
      })
      .addCase(disconnectWABA.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, resetWABA } = wabaSlice.actions;
export default wabaSlice.reducer;
